import React, { useState } from "react";
import { Column, Icon, Row, Text } from "@amzn/meridian";
import chevronUpSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-up-small";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import { COLORS } from "src/constants/colors";
import { Result } from "src/data/types";
import ResultInstanceCard from "src/components/UI/ResultInstanceCard";
import PhotoReviewSheet from "./PhotoReviewSheet";
import { useAppSelector } from "src/store/store";


type QuestionResultGroupProps = {
  questionText: string;
  questionOrder: number;
  inspectionId: string;
  instances: Array<{
    instanceNumber: number;
    evidenceUri: string;
    result: Result;
  }>;
  onUpdate?: () => void;
};

const QuestionResultGroup = ({
  questionText,
  questionOrder,
  inspectionId,
  instances,
  onUpdate,
}: QuestionResultGroupProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedResult, setSelectedResult] = useState<Result | null>(null);
  const hasMultipleInstances = instances.length > 1;

  // Get presigned URL map from Redux
  const presignedUrlMap = useAppSelector((state) => state.inspection.presignedUrlMap);
  
  // Helper to get presigned URL or fallback to S3 URI
  const getUrl = (s3Uri: string): string => {
    return presignedUrlMap[s3Uri] || s3Uri;
  };

  return (
    <Column backgroundColor={COLORS.WHITE} spacing="200" spacingInset="400">
      {/* Question Header - Clickable */}
      <div
        style={{ cursor: "pointer" }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Text type="h300">
            {questionOrder}. {questionText}
          </Text>
          <Row spacing="200" alignmentVertical="center">
            <Text type="b100" color="secondary">
              {instances.length} {instances.length === 1 ? "instance" : "instances"}
            </Text>
            <Icon
              tokens={isExpanded ? chevronUpSmallTokens : chevronDownSmallTokens}
              color="primary"
            />
          </Row>
        </Row>
      </div>

      {/* Instances - Collapsible */}
      {isExpanded && (
        <Column spacing="200" spacingInset="none 400">
          {instances.map((instance) => (
            <ResultInstanceCard
              key={instance.evidenceUri}
              instanceNumber={instance.instanceNumber}
              evidenceUri={getUrl(instance.evidenceUri)}
              result={instance.result}
              showInstanceNumber={hasMultipleInstances}
              onClick={() => setSelectedResult(instance.result)}
            />
          ))}
        </Column>
      )}
      
      {/* Photo Review Sheet */}
      {selectedResult && (
        <PhotoReviewSheet
          open={!!selectedResult}
          onClose={() => setSelectedResult(null)}
          result={selectedResult}
          evidencePresignedUrl={getUrl(selectedResult.evidence?.[0]?.uri || "")}
          inspectionId={inspectionId}
          onUpdate={onUpdate}
        />
      )}
    </Column>
  );
};

export default QuestionResultGroup;
